import { ApiError, clearSession, readSession, saveSession, type Api, type Me, type StoredSession } from "@eldrun/api-client";
import { setTutorialEnabled } from "../ui/hud/tutorial-model.ts";
import { loadTutorial, onTutorialSaved, saveTutorial, tutorialFinished } from "../ui/hud/tutorial-store.ts";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Conquistas que a partida deu a esta conta, ou "não deu para saber agora". */
export type MatchAchievements = { kind: "ok"; names: string[] } | { kind: "indisponivel" };

export interface PollOptions {
  /** Desiste depois deste tempo (ms) sem a partida gravada. */
  deadlineMs?: number;
  intervalMs?: number;
  signal?: AbortSignal;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/** `localStorage` quando o navegador deixa; senão, só na memória desta aba. */
export function browserStore(): Store {
  try {
    const probe = "eldrun:teste";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return { getItem: (k) => mem.get(k) ?? null, setItem: (k, v) => void mem.set(k, v), removeItem: (k) => void mem.delete(k) };
  }
}

/**
 * Conta no jogo (F34): login contra a API, sessão no navegador do jogo (a mesma forma do site, em
 * outra origem), conferência ao abrir o menu, conquistas da partida e o tutorial espelhado na conta
 * (doc 06). Convidado é quem não tem sessão: joga igual, mas não pontua (ADR 0005).
 *
 * Toda resposta da API vale só para a sessão que a pediu: outra aba pode ter entrado em outra conta
 * ou saído no meio (o `localStorage` é dividido entre as abas).
 */
export class Account {
  private readonly api: Api;
  private readonly store: Store;
  /** Trazendo o tutorial da conta: a gravação local não volta para a API. */
  private pulling = false;
  /** Sobe a cada login, saída ou cancelamento: um login que termina depois não vale. */
  private generation = 0;
  /** Gravação do tutorial a fazer: a última escolha vence; falha fica para a próxima conferência. */
  private pendingTutorial: { token: string; done: boolean } | undefined;
  private sending = false;

  constructor(api: Api, store: Store) {
    this.api = api;
    this.store = store;
    // Concluir, desligar ou religar o tutorial com a conta conectada grava na conta (só quando muda).
    onTutorialSaved((save, previous) => {
      const s = this.session();
      const done = tutorialFinished(save);
      if (!s || this.pulling || done === tutorialFinished(previous)) return;
      this.queueTutorial(s.token, done);
    });
  }

  session(): StoredSession | undefined {
    return readSession(this.store);
  }

  private isCurrent(token: string): boolean {
    return this.session()?.token === token;
  }

  /**
   * Entra e guarda a sessão; o tutorial da conta e o deste navegador se acertam. Devolve
   * `undefined` se o login foi cancelado (ou houve saída) enquanto a API respondia.
   */
  async login(login: string, senha: string): Promise<StoredSession | undefined> {
    const generation = ++this.generation;
    const s = await this.api.login(login.trim(), senha);
    if (generation !== this.generation) return undefined;
    if (!saveSession(this.store, s.token, s.usuario.apelido)) throw new ApiError(0, "sessao", "Este navegador não guardou a sessão.");
    const session = this.session()!;
    try {
      const me = await this.api.me(session.token);
      if (this.isCurrent(session.token)) this.syncTutorial(me, session.token);
    } catch {
      // Sem `/me` agora: o tutorial se acerta na próxima conferência.
    }
    return session;
  }

  /** O formulário de login foi fechado: um login em andamento não vale mais. */
  cancel(): void {
    this.generation++;
  }

  logout(): void {
    this.generation++;
    this.pendingTutorial = undefined;
    clearSession(this.store);
  }

  /**
   * Confere a sessão guardada (ao abrir o menu): recusada pela API (401), sai; sem rede, mantém
   * (o servidor de jogo confere de novo ao entrar). Acerta o tutorial e manda o que ficou pendente.
   */
  async check(): Promise<Me | undefined> {
    const s = this.session();
    if (!s) return undefined;
    try {
      const me = await this.api.me(s.token);
      if (!this.isCurrent(s.token)) return undefined;
      this.syncTutorial(me, s.token);
      void this.flushTutorial();
      return me;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && this.isCurrent(s.token)) this.logout();
      return undefined;
    }
  }

  /**
   * A conta vence: concluída (`true`) desliga o tutorial daqui; religada (`false`) recomeça o
   * daqui, se estava concluído; sem registro (`null`), o concluído daqui vai para a conta.
   */
  private syncTutorial(me: Me, token: string): void {
    const local = loadTutorial();
    const done = tutorialFinished(local);
    if (me.tutorialConcluido === null) {
      if (done) this.queueTutorial(token, true);
      return;
    }
    if (me.tutorialConcluido === done) return;
    this.pulling = true;
    try {
      saveTutorial(me.tutorialConcluido ? setTutorialEnabled(local, false) : { v: 1, enabled: true, done: [], epoch: local.epoch + 1 });
    } finally {
      this.pulling = false;
    }
  }

  private queueTutorial(token: string, done: boolean): void {
    this.pendingTutorial = { token, done };
    void this.flushTutorial();
  }

  /** Uma gravação por vez, sempre a mais nova (duas escolhas seguidas não chegam fora de ordem). */
  private async flushTutorial(): Promise<void> {
    if (this.sending) return;
    this.sending = true;
    try {
      while (this.pendingTutorial) {
        const job = this.pendingTutorial;
        if (!this.isCurrent(job.token)) {
          this.pendingTutorial = undefined;
          break;
        }
        try {
          await this.api.setTutorial(job.token, job.done);
        } catch {
          break;
        }
        if (this.pendingTutorial === job) this.pendingTutorial = undefined;
      }
    } finally {
      this.sending = false;
    }
  }

  /**
   * Conquistas que a partida deu a `userId` (a conta que jogou, mesmo que a sessão tenha vencido
   * depois: a consulta é pública). O servidor envia o resultado ao fim, com novas tentativas (1, 3 e
   * 9 s), e a API grava a partida antes das conquistas: enquanto responde 404, a lista não está
   * pronta, falha de rede ou 5xx, tenta de novo até o prazo; outro erro desiste na hora.
   */
  async achievements(matchId: string, userId: string, options: PollOptions = {}): Promise<MatchAchievements> {
    const now = options.now ?? Date.now;
    const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    const interval = options.intervalMs ?? 2_000;
    const deadline = now() + (options.deadlineMs ?? 30_000);
    for (;;) {
      if (options.signal?.aborted) return { kind: "indisponivel" };
      let retry = true;
      try {
        const m = await this.api.match(matchId);
        if (m.conquistasProntas !== false) return { kind: "ok", names: m.conquistas.filter((c) => c.usuarioId === userId).map((c) => c.nome) };
      } catch (err) {
        retry = !(err instanceof ApiError) || err.status === 0 || err.status === 404 || err.status >= 500;
      }
      if (!retry || now() + interval > deadline) return { kind: "indisponivel" };
      await sleep(interval);
    }
  }
}
